/**
 * Coups de pouce — Attendu 1064 « Placer sur une demi-droite graduée un point
 * dont l'abscisse est un nombre décimal ».
 *
 * placer_* (trois formats interactifs) : entrée PARTAGÉE — lire la graduation,
 * séparer partie entière et partie décimale, attention aux zéros (les lignes
 * « centièmes sur un intervalle de 0,1 » et « vérifie l'encadrement » ont été
 * retirées à la demande du prof, 2026-09-06). L'exemple (revue
 * 2026-09-05) : placer 2,7 pas à pas, puis le piège 2,07. SVG piloté par les
 * flèches de la sheet (data-etapes / data-caption).
 *
 * lire_* (QCM, saisie, V/F) : entrée PARTAGÉE — valeur d'une graduation,
 * compter après l'entier, un centième ne tombe pas sur un dixième, l'unité en
 * 4 (la ligne « comparer pour ordonner et prolonger » a été retirée à la
 * demande du prof, 2026-09-06). L'exemple : 0,25 au milieu de 0,2 et 0,3
 * (dixièmes), puis 2,3 entre 2,25 et 2,5 (quarts).
 */

const X = (v) => 30 + 70 * v;
const GRAD = () => {
    let s = '';
    for (let i = 0; i <= 40; i++) {
        const gros = i % 10 === 0; const x = X(i / 10).toFixed(1);
        s += `<line x1="${x}" y1="${110 - (gros ? 9 : 5)}" x2="${x}" y2="${110 + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${x}" y="134" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${i / 10}</text>`;
    }
    return s;
};

const SVG_PLACER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Placer le point d'abscisse 2,7 sur une demi-droite graduée de 0 à 4 partagée en dixièmes, en 3 étapes à faire défiler avec les flèches. Étape 2 : 2 et 7 dixièmes, on part de la graduation 2 et on avance de 7 petites graduations. Étape 3 : le piège 2,07, c'est 2 et 7 centièmes, tout près de 2, entre 2 et 2,1. Partie entière d'abord, puis les dixièmes">
  <style>
    @keyframes cp1064-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1064-pop { opacity: 0; animation: cp1064-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Placer le point d'abscisse 2,7</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">2, puis 7 petites graduations</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Le piège : 2,07 n'est pas 2,7</text>

  <!-- ── La demi-droite (étape 1) ───────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="22" y1="110" x2="336" y2="110" stroke="#1e293b" stroke-width="1.6"/>
    <polygon points="336,110 328,106 328,114" fill="#1e293b"/>
    ${GRAD()}
    <text x="180" y="58" font-size="11" font-weight="600" fill="#475569" text-anchor="middle">chaque unité est partagée en 10 : une petite graduation vaut 0,1</text>
  </g>

  <!-- ── Partie entière, puis les dixièmes (étape 2) ────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1064-pop">
      <circle cx="${X(2)}" cy="130" r="9" fill="none" stroke="#1D9E75" stroke-width="2"/>
      ${[1, 2, 3, 4, 5, 6, 7].map((k) => `<text x="${X(2 + k / 10).toFixed(1)}" y="96" font-size="8.5" font-weight="700" fill="#1D9E75" text-anchor="middle">${k}</text>`).join('')}
      <line x1="${X(2)}" y1="110" x2="${X(2.7)}" y2="110" stroke="#1D9E75" stroke-width="3"/>
      <circle cx="${X(2.7)}" cy="110" r="4.2" fill="#1D9E75"/>
      <text x="${X(2.7)}" y="80" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">2,7</text>
      <text x="8" y="165" font-size="11.5" font-weight="700" fill="#1D9E75">2,7 c'est 2 et 7 dixièmes :</text>
      <text x="8" y="180" font-size="11.5" font-weight="700" fill="#1D9E75">partie entière 2, on part de la graduation 2,</text>
      <text x="8" y="195" font-size="11.5" font-weight="700" fill="#1D9E75">puis on avance de 7 petites graduations ✓</text>
    </g>
  </g>

  <!-- ── Le piège 2,07 (étape 3) ────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1064-pop">
      <circle cx="${X(2.07).toFixed(1)}" cy="110" r="4.2" fill="#B45309"/>
      <text x="${(X(2.07) + 6).toFixed(1)}" y="150" font-size="11.5" font-weight="800" fill="#B45309" text-anchor="middle">2,07</text>
      <text x="8" y="215" font-size="11.5" font-weight="700" fill="#B45309">2,07 c'est 2 et 7 centièmes : tout près de 2,</text>
      <text x="8" y="230" font-size="11.5" font-weight="700" fill="#B45309">entre 2 et 2,1. Ce n'est pas 2,7 ✗</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Partie entière d'abord, puis les dixièmes.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_PLACER = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Lis d'abord la graduation** : regarde en combien chaque unité est partagée. En 10, chaque petite graduation vaut 0,1 ; en 4, elle vaut 0,25 ; en 2, 0,5 ; en 5, 0,2.",
                "**Sépare la partie entière et la partie décimale** : 2,7 c'est 2 et 7 dixièmes. On part de la graduation 2, on avance de 7 petites graduations.",
                "**Attention aux zéros** : 2,07 c'est 2 et 7 centièmes, tout près de 2, pas 7 dixièmes.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_PLACER },
    ],
};

// Deux demi-droites courtes : en haut 0 → 1 partagée en 10 (x de 40 à 320), en bas 2 → 3 partagée en 4
const XH = (v) => 40 + 280 * v;            // v ∈ [0, 1]
const XB = (v) => 40 + 280 * (v - 2);      // v ∈ [2, 3]
const LIGNE = (y, X, n, etiquettes) => {
    let s = `<line x1="32" y1="${y}" x2="336" y2="${y}" stroke="#1e293b" stroke-width="1.6"/><polygon points="336,${y} 328,${y - 4} 328,${y + 4}" fill="#1e293b"/>`;
    for (let i = 0; i <= n; i++) {
        const gros = i === 0 || i === n; const x = X(etiquettes.debut + i / n).toFixed(1);
        s += `<line x1="${x}" y1="${y - (gros ? 9 : 5)}" x2="${x}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        const txt = etiquettes.noms[i]; if (txt) s += `<text x="${x}" y="${y + 22}" font-size="${gros ? 12 : 9.5}" font-weight="${gros ? 700 : 600}" fill="#1e293b" text-anchor="middle">${txt}</text>`;
    }
    return s;
};

const SVG_LIRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Lire une graduation, en 3 étapes à faire défiler avec les flèches. Deux demi-droites : de 0 à 1 partagée en 10, et de 2 à 3 partagée en 4. Étape 2 : 0,25 est entre 0,2 et 0,3, exactement au milieu, aucune graduation ne l'atteint. Étape 3 : 2,3 est entre 2,25 et 2,5, tout près de 2,25, sur aucune graduation ; 2,5, lui, tombe sur la 2e graduation. D'abord la valeur d'une graduation, puis on encadre">
  <style>
    @keyframes cp1064-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1064-pop { opacity: 0; animation: cp1064-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Où se placent 0,25 et 2,3 ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">0,25 : entre 0,2 et 0,3, au milieu</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">2,3 : entre 2,25 et 2,5, sur aucune graduation</text>

  <!-- ── Les deux graduations (étape 1) ─────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="36" font-size="10.5" font-weight="600" fill="#475569">unité partagée en 10 : une graduation vaut 0,1</text>
    ${LIGNE(66, XH, 10, { debut: 0, noms: ['0', '', '', '', '', '', '', '', '', '', '1'] })}
    <text x="8" y="118" font-size="10.5" font-weight="600" fill="#475569">unité partagée en 4 : une graduation vaut 0,25</text>
    ${LIGNE(152, XB, 4, { debut: 2, noms: ['2', '2,25', '2,5', '2,75', '3'] })}
  </g>

  <!-- ── 0,25 en dixièmes (étape 2) ─────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1064-pop">
      <circle cx="${XH(0.2)}" cy="66" r="8" fill="none" stroke="#1D9E75" stroke-width="2"/>
      <circle cx="${XH(0.3)}" cy="66" r="8" fill="none" stroke="#1D9E75" stroke-width="2"/>
      <text x="${XH(0.2)}" y="88" font-size="9.5" font-weight="700" fill="#1D9E75" text-anchor="middle">0,2</text>
      <text x="${XH(0.3)}" y="88" font-size="9.5" font-weight="700" fill="#1D9E75" text-anchor="middle">0,3</text>
      <circle cx="${XH(0.25)}" cy="66" r="4.2" fill="#1D9E75"/>
      <text x="${XH(0.25)}" y="50" font-size="11.5" font-weight="800" fill="#1D9E75" text-anchor="middle">0,25</text>
      <text x="8" y="192" font-size="11.5" font-weight="700" fill="#1D9E75">0,25 est entre 0,2 et 0,3, exactement au milieu :</text>
      <text x="8" y="207" font-size="11.5" font-weight="700" fill="#1D9E75">aucune graduation en dixièmes ne l'atteint.</text>
    </g>
  </g>

  <!-- ── 2,3 en quarts (étape 3) ─────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1064-pop">
      <circle cx="${XB(2.25)}" cy="152" r="8" fill="none" stroke="#B45309" stroke-width="2"/>
      <circle cx="${XB(2.5)}" cy="152" r="8" fill="none" stroke="#B45309" stroke-width="2"/>
      <circle cx="${XB(2.3)}" cy="152" r="4.2" fill="#B45309"/>
      <text x="${XB(2.3)}" y="136" font-size="11.5" font-weight="800" fill="#B45309" text-anchor="middle">2,3</text>
      <text x="8" y="226" font-size="11.5" font-weight="700" fill="#B45309">2,3 est entre 2,25 et 2,5, tout près de 2,25 :</text>
      <text x="8" y="240" font-size="11.5" font-weight="700" fill="#B45309">sur aucune graduation. 2,5, lui, tombe sur la 2e.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_LIRE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Trouve la valeur d'une petite graduation** : l'unité partagée en 10 donne 0,1 ; en 4, 0,25 ; en 2, 0,5 ; en 5, 0,2. C'est l'écart entre deux graduations voisines.",
                "**Pour un nombre à une décimale sur une graduation en dixièmes**, compte les petites graduations après l'entier : 2,7 est à la 7e après 2.",
                "**Un nombre à deux décimales ne tombe pas sur un dixième** : 0,25 est entre 0,2 et 0,3, exactement au milieu. Pour l'atteindre, il faudrait partager chaque dixième en 10.",
                "**Si l'unité est partagée en 4**, seuls 0,25, 0,5 et 0,75 tombent sur les graduations : 2,5 est sur la 2e après 2, mais 2,3 est entre 2,25 et 2,5, sur aucune graduation.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_LIRE },
    ],
};

const CP_1064 = {
    placer_dixiemes_placer_points: AIDE_PLACER,
    placer_centiemes_placer_points: AIDE_PLACER,
    placer_pas_placer_points: AIDE_PLACER,
    lire_qcm_unique: AIDE_LIRE,
    lire_saisie: AIDE_LIRE,
    lire_tableau_vf: AIDE_LIRE,
};

export default CP_1064;
